import React, { useState, useEffect } from 'react';
import { Add, Edit, DeleteOutline, Check, EventRepeat } from '@mui/icons-material';
import api, { goalService } from '../services/api';
import { PillButton } from './ui/Buttons';

interface Goal {
  id: number;
  title: string;
  description: string;
  employeeId: number;
  employeeName: string;
  managerId?: number;
  managerName?: string;
  startDate: string;
  endDate: string;
  status: string;
  priority: string;
  weightage: number;
  progress: number;
  employeeComments?: string;
  managerComments?: string;
  createdAt: string;
}

const GoalManagement: React.FC = () => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateGoal, setShowCreateGoal] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('all');
  const [expandedEmployees, setExpandedEmployees] = useState<Set<number>>(new Set());
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    employeeIds: [] as number[],
    startDate: '',
    endDate: '',
    priority: 'Medium',
    weightage: 0
  });
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = localStorage.getItem('userRole') ||
                   localStorage.getItem('role') ||
                   currentUser.role;

  const isManager = userRole === 'Manager' || userRole === 'Admin';
  const isAdmin = userRole === 'Admin';

  useEffect(() => {
    fetchGoals();
    if (isManager) {
      fetchUsers();
    }
  }, []);

  const fetchGoals = async () => {
    try {
      const response = await goalService.getGoals();
      setGoals(response);
    } catch (error) {
      console.error('Error fetching goals:', error);
      setGoals([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      // Active employees only (admins are excluded server-side)
      const response = await api.get('/users/employees');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleCreateGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newGoal.employeeIds.length === 0) {
      alert('Select at least one employee.');
      return;
    }
    try {
      const response = await goalService.createGoal({
        ...newGoal,
        startDate: new Date(newGoal.startDate).toISOString(),
        endDate: new Date(newGoal.endDate).toISOString(),
        weightage: parseInt(newGoal.weightage.toString())
      });
      setShowCreateGoal(false);
      setNewGoal({ title: '', description: '', employeeIds: [], startDate: '', endDate: '', priority: 'Medium', weightage: 0 });
      setDepartmentFilter('all');
      fetchGoals();
      const count = Array.isArray(response) ? response.length : 1;
      alert(`Goal created for ${count} employee(s)!`);
    } catch (error) {
      console.error('Error creating goal:', error);
      alert('Error creating goal. Please try again.');
    }
  };

  const toggleEmployeeSelection = (employeeId: number) => {
    setNewGoal(prev => ({
      ...prev,
      employeeIds: prev.employeeIds.includes(employeeId)
        ? prev.employeeIds.filter(id => id !== employeeId)
        : [...prev.employeeIds, employeeId]
    }));
  };

  const goalTemplates = Array.from(
    new Map(goals.map(g => [`${g.title}|${g.description}`, g])).values()
  ).sort((a, b) => a.title.localeCompare(b.title));

  const applyGoalTemplate = (goalId: string) => {
    if (!goalId) return;
    const template = goals.find(g => g.id === parseInt(goalId));
    if (!template) return;
    setNewGoal(prev => ({
      ...prev,
      title: template.title,
      description: template.description,
      priority: template.priority,
      weightage: template.weightage
    }));
  };

  const departments = Array.from(new Set(users.map(u => u.department || 'Unassigned'))).sort();

  const visibleUsers = departmentFilter === 'all'
    ? users
    : users.filter(u => (u.department || 'Unassigned') === departmentFilter);

  const selectAllVisible = () => {
    const visibleIds = visibleUsers.map(u => u.id);
    setNewGoal(prev => ({
      ...prev,
      employeeIds: Array.from(new Set([...prev.employeeIds, ...visibleIds]))
    }));
  };

  const clearAllVisible = () => {
    const visibleIds = new Set(visibleUsers.map(u => u.id));
    setNewGoal(prev => ({
      ...prev,
      employeeIds: prev.employeeIds.filter(id => !visibleIds.has(id))
    }));
  };

  const selectMyTeam = () => {
    const teamIds = users.filter(u => u.managerId === currentUser.id).map(u => u.id);
    setNewGoal(prev => ({
      ...prev,
      employeeIds: Array.from(new Set([...prev.employeeIds, ...teamIds]))
    }));
  };

  const handleUpdateGoal = async (goalId: number, updates: any) => {
    try {
      await goalService.updateGoal(goalId, updates);
      fetchGoals();
      setEditingGoal(null);
      alert('Goal updated successfully!');
    } catch (error) {
      console.error('Error updating goal:', error);
      alert('Error updating goal. Please try again.');
    }
  };

  const createHalfYearlyGoals = async () => {
    try {
      const response = await goalService.createHalfYearlyGoals();
      alert(response.message || 'Half-yearly goals created successfully!');
      fetchGoals();
    } catch (error) {
      console.error('Error creating half-yearly goals:', error);
      alert('Error creating half-yearly goals. Please try again.');
    }
  };

  const handleDeleteGoal = async (goalId: number) => {
    if (window.confirm('Are you sure you want to delete this goal?')) {
      try {
        await goalService.deleteGoal(goalId);
        fetchGoals();
        alert('Goal deleted successfully!');
      } catch (error) {
        console.error('Error deleting goal:', error);
        alert('Error deleting goal. Please try again.');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return '#059669';
      case 'Completed': return '#7c3aed';
      case 'Cancelled': return '#e11d48';
      default: return '#64748b';
    }
  };

  const toggleEmployee = (employeeId: number) => {
    const newExpanded = new Set(expandedEmployees);
    if (newExpanded.has(employeeId)) {
      newExpanded.delete(employeeId);
    } else {
      newExpanded.add(employeeId);
    }
    setExpandedEmployees(newExpanded);
  };

  const groupedGoals = goals.reduce((acc, goal) => {
    if (!acc[goal.employeeId]) {
      acc[goal.employeeId] = { employeeName: goal.employeeName, goals: [] };
    }
    acc[goal.employeeId].goals.push(goal);
    return acc;
  }, {} as Record<number, { employeeName: string; goals: Goal[] }>);

  const filteredGrouped = Object.entries(groupedGoals).filter(([empId]) => 
    selectedEmployee === 'all' || empId === selectedEmployee
  );

  const renderGoalCard = (goal: Goal) => (
    <div key={goal.id} className="goal-card">
      <div className="goal-header">
        <h3>{goal.title}</h3>
        <span className="status" style={{ backgroundColor: getStatusColor(goal.status) }}>
          {goal.status}
        </span>
      </div>
      <p>{goal.description}</p>
      <div className="goal-details">
        <div><strong>Employee:</strong> {goal.employeeName}</div>
        <div><strong>Manager:</strong> {goal.managerName || 'Not assigned'}</div>
        <div><strong>Period:</strong> {new Date(goal.startDate).toLocaleDateString()} - {new Date(goal.endDate).toLocaleDateString()}</div>
        <div><strong>Priority:</strong> {goal.priority}</div>
        <div><strong>Weightage:</strong> {goal.weightage}%</div>
        <div><strong>Progress:</strong> {goal.progress}%</div>
      </div>
      
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${goal.progress}%` }}></div>
      </div>

      {goal.employeeComments && (
        <div className="comments">
          <strong>Employee Comments:</strong>
          <p>{goal.employeeComments}</p>
        </div>
      )}

      {goal.managerComments && (
        <div className="comments">
          <strong>Manager Comments:</strong>
          <p>{goal.managerComments}</p>
        </div>
      )}

      <div className="goal-actions">
        <PillButton variant="warning" size="sm" onClick={() => setEditingGoal(goal)}>
          <Edit sx={{ fontSize: 14 }} /> Edit
        </PillButton>
        {isAdmin && (
          <PillButton variant="danger" size="sm" onClick={() => handleDeleteGoal(goal.id)} className="ml-2">
            <DeleteOutline sx={{ fontSize: 14 }} /> Delete
          </PillButton>
        )}
      </div>
    </div>
  );

  if (loading) return <div>Loading goals...</div>;

  return (
    <div className="goal-management">
      <div className="header">
        <h2>Goal Management</h2>
        <div className="actions">
          {isManager && (
            <>
              <PillButton variant="primary" onClick={() => setShowCreateGoal(true)}>
                <Add sx={{ fontSize: 16 }} /> Create Goal
              </PillButton>
              <PillButton variant="success" onClick={createHalfYearlyGoals}>
                <EventRepeat sx={{ fontSize: 16 }} /> Create Half-Yearly Goals
              </PillButton>
            </>
          )}
        </div>
      </div>

      {isManager && goals.length > 0 && (
        <div className="filter-section">
          <label>Filter by Employee:</label>
          <select value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)}>
            <option value="all">All Employees</option>
            {Object.entries(groupedGoals).map(([empId, data]) => (
              <option key={empId} value={empId}>{data.employeeName}</option>
            ))}
          </select>
        </div>
      )}

      <div className="goals-list">
        {goals.length === 0 ? (
          <div className="no-goals">
            <p>No goals found. {isManager ? 'Create some goals for your team members.' : 'Your manager will assign goals to you.'}</p>
          </div>
        ) : isManager ? (
          filteredGrouped.map(([employeeId, data]) => (
            <div key={employeeId} className="employee-group">
              <div className="employee-header" onClick={() => toggleEmployee(parseInt(employeeId))}>
                <span className="toggle-icon">{expandedEmployees.has(parseInt(employeeId)) ? '▼' : '▶'}</span>
                <h3>{data.employeeName}</h3>
                <span className="goal-count">{data.goals.length} goal(s)</span>
              </div>
              {expandedEmployees.has(parseInt(employeeId)) && (
                <div className="employee-goals">
                  {data.goals.map(goal => renderGoalCard(goal))}
                </div>
              )}
            </div>
          ))
        ) : (
          goals.map(goal => renderGoalCard(goal))
        )}
      </div>

      {showCreateGoal && (
        <div className="modal">
          <div className="modal-content">
            <h3>Create New Goal</h3>
            <form onSubmit={handleCreateGoal}>
              {goalTemplates.length > 0 && (
                <div className="form-group">
                  <label>Reuse Existing Goal (optional):</label>
                  <select defaultValue="" onChange={(e) => applyGoalTemplate(e.target.value)}>
                    <option value="">-- Start from scratch --</option>
                    {goalTemplates.map(template => (
                      <option key={template.id} value={template.id}>{template.title}</option>
                    ))}
                  </select>
                  <small style={{ color: '#94a3b8' }}>Prefills title, description, priority and weightage below — then just pick the employee(s) to assign it to.</small>
                </div>
              )}
              <div className="form-group">
                <label>Title:</label>
                <input
                  type="text"
                  value={newGoal.title}
                  onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description:</label>
                <textarea
                  value={newGoal.description}
                  onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Employees ({newGoal.employeeIds.length} selected):</label>
                <div className="employee-picker-toolbar">
                  <select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
                    <option value="all">All Departments</option>
                    {departments.map(dep => (
                      <option key={dep} value={dep}>{dep}</option>
                    ))}
                  </select>
                  <PillButton type="button" variant="outline" size="sm" onClick={selectAllVisible}>
                    Select {departmentFilter === 'all' ? 'All' : 'Group'}
                  </PillButton>
                  <PillButton type="button" variant="outline" size="sm" onClick={clearAllVisible}>Clear</PillButton>
                  {userRole === 'Manager' && (
                    <PillButton type="button" variant="outline" size="sm" onClick={selectMyTeam}>My Team</PillButton>
                  )}
                </div>
                <div className="employee-picker-list">
                  {visibleUsers.length === 0 ? (
                    <div className="employee-picker-empty">No active employees found.</div>
                  ) : (
                    visibleUsers.map(user => (
                      <label key={user.id} className="employee-picker-item">
                        <input
                          type="checkbox"
                          checked={newGoal.employeeIds.includes(user.id)}
                          onChange={() => toggleEmployeeSelection(user.id)}
                        />
                        <span>{user.fullName}</span>
                        <span className="employee-picker-dept">{user.department || 'Unassigned'}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Start Date:</label>
                <input
                  type="date"
                  value={newGoal.startDate}
                  onChange={(e) => setNewGoal({...newGoal, startDate: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>End Date:</label>
                <input
                  type="date"
                  value={newGoal.endDate}
                  onChange={(e) => setNewGoal({...newGoal, endDate: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Priority:</label>
                <select
                  value={newGoal.priority}
                  onChange={(e) => setNewGoal({...newGoal, priority: e.target.value})}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>
              <div className="form-group">
                <label>Weightage (%):</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={newGoal.weightage}
                  onChange={(e) => setNewGoal({...newGoal, weightage: parseInt(e.target.value) || 0})}
                  required
                />
              </div>
              <div className="form-actions">
                <PillButton type="submit" variant="success">Create Goal</PillButton>
                <PillButton type="button" variant="neutral" onClick={() => setShowCreateGoal(false)}>Cancel</PillButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingGoal && (
        <div className="modal">
          <div className="modal-content">
            <h3>Edit Goal</h3>
            <GoalEditForm 
              goal={editingGoal} 
              onSave={handleUpdateGoal} 
              onCancel={() => setEditingGoal(null)}
              userRole={userRole}
            />
          </div>
        </div>
      )}

      <style>{`
        .goal-management { padding: 20px; min-height: 400px; }
        .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; border-bottom: 2px solid #f1f5f9; padding-bottom: 15px; }
        .header h2 { margin: 0; color: #1e293b; }
        .actions { display: flex; gap: 10px; }
        .filter-section { margin: 20px 0; padding: 15px; background: #f8fafc; border-radius: 16px; }
        .filter-section label { margin-right: 10px; font-weight: 600; color: #475569; }
        .filter-section select { padding: 8px 12px; border: 1px solid #e2e8f0; border-radius: 10px; font-size: 14px; }
        .employee-group { margin: 20px 0; border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; }
        .employee-header { display: flex; align-items: center; gap: 10px; padding: 15px 20px; background: #f8fafc; cursor: pointer; transition: background 0.2s; }
        .employee-header:hover { background: #e2e8f0; }
        .employee-header h3 { margin: 0; flex: 1; color: #1e293b; font-size: 18px; }
        .toggle-icon { font-size: 12px; color: #64748b; }
        .goal-count { background: #7c3aed; color: white; padding: 4px 12px; border-radius: 9999px; font-size: 12px; font-weight: 600; }
        .employee-goals { padding: 10px; background: white; }
        .no-goals { text-align: center; padding: 40px; background: #f8fafc; border-radius: 16px; margin: 20px 0; }
        .no-goals p { font-size: 16px; color: #64748b; margin: 0; }
        .goal-card { border: 1px solid #e2e8f0; padding: 20px; margin: 15px 0; border-radius: 16px; background: white; box-shadow: 0 2px 4px rgba(15,23,42,0.08); }
        .goal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
        .goal-header h3 { margin: 0; color: #1e293b; }
        .status { padding: 4px 8px; color: white; border-radius: 10px; font-size: 12px; font-weight: bold; }
        .goal-details { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; margin: 15px 0; }
        .goal-details div { padding: 5px 0; }
        .progress-bar { width: 100%; height: 20px; background: #f1f5f9; border-radius: 10px; overflow: hidden; margin: 10px 0; }
        .progress-fill { height: 100%; background: #059669; transition: width 0.3s ease; }
        .comments { margin: 10px 0; padding: 10px; background: #f8fafc; border-radius: 10px; border-left: 4px solid #7c3aed; }
        .goal-actions { margin-top: 15px; display: flex; }
        .modal { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(2,6,23,0.6); display: flex; justify-content: center; align-items: center; z-index: 1000; }
        .modal-content { background: white; padding: 20px; border-radius: 16px; width: 500px; max-height: 80vh; overflow-y: auto; box-shadow: 0 4px 6px rgba(15,23,42,0.08); }
        .modal-content h3 { margin-top: 0; color: #1e293b; }
        .form-group { margin-bottom: 15px; }
        .form-group label { display: block; margin-bottom: 5px; font-weight: bold; color: #475569; }
        .form-group input, .form-group select, .form-group textarea { width: 100%; padding: 8px; border: 1px solid #e2e8f0; border-radius: 10px; font-size: 14px; }
        .form-group textarea { min-height: 80px; resize: vertical; }
        .employee-picker-toolbar { display: flex; gap: 8px; margin-bottom: 8px; flex-wrap: wrap; align-items: center; }
        .employee-picker-toolbar select { width: auto; flex: 1; min-width: 140px; }
        .employee-picker-list { max-height: 200px; overflow-y: auto; border: 1px solid #e2e8f0; border-radius: 10px; padding: 6px; }
        .employee-picker-item { display: flex; align-items: center; gap: 8px; padding: 6px 4px; cursor: pointer; font-weight: normal; }
        .employee-picker-item:hover { background: #f8fafc; }
        .employee-picker-item input { width: auto; }
        .employee-picker-dept { margin-left: auto; color: #94a3b8; font-size: 12px; }
        .employee-picker-empty { padding: 10px; color: #94a3b8; font-size: 13px; }
        .form-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
      `}</style>
    </div>
  );
};

const GoalEditForm: React.FC<{ goal: Goal; onSave: (id: number, updates: any) => void; onCancel: () => void; userRole: string | null }> = ({ goal, onSave, onCancel, userRole }) => {
  const [updates, setUpdates] = useState({
    title: goal.title,
    description: goal.description,
    weightage: goal.weightage,
    progress: goal.progress,
    employeeComments: goal.employeeComments || '',
    managerComments: goal.managerComments || '',
    status: goal.status
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(goal.id, updates);
  };

  return (
    <form onSubmit={handleSubmit}>
      {(userRole === 'Manager' || userRole === 'Admin') && (
        <>
          <div className="form-group">
            <label>Title:</label>
            <input
              type="text"
              value={updates.title}
              onChange={(e) => setUpdates({...updates, title: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label>Description:</label>
            <textarea
              value={updates.description}
              onChange={(e) => setUpdates({...updates, description: e.target.value})}
              rows={3}
            />
          </div>
          <div className="form-group">
            <label>Weightage (%):</label>
            <input
              type="number"
              min="0"
              max="100"
              value={updates.weightage}
              onChange={(e) => setUpdates({...updates, weightage: parseInt(e.target.value)})}
            />
          </div>
        </>
      )}
      <div className="form-group">
        <label>Progress (%):</label>
        <input
          type="number"
          min="0"
          max="100"
          value={updates.progress}
          onChange={(e) => setUpdates({...updates, progress: parseInt(e.target.value)})}
        />
      </div>
      
      {userRole === 'Employee' && (
        <div className="form-group">
          <label>Employee Comments:</label>
          <textarea
            value={updates.employeeComments}
            onChange={(e) => setUpdates({...updates, employeeComments: e.target.value})}
            rows={4}
          />
        </div>
      )}
      
      {(userRole === 'Manager' || userRole === 'Admin') && (
        <>
          <div className="form-group">
            <label>Manager Comments:</label>
            <textarea
              value={updates.managerComments}
              onChange={(e) => setUpdates({...updates, managerComments: e.target.value})}
              rows={4}
            />
          </div>
          <div className="form-group">
            <label>Status:</label>
            <select
              value={updates.status}
              onChange={(e) => setUpdates({...updates, status: e.target.value})}
            >
              <option value="Active">Active</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
        </>
      )}
      
      <div className="form-actions">
        <PillButton type="submit" variant="success">
          <Check sx={{ fontSize: 14 }} /> Save Changes
        </PillButton>
        <PillButton type="button" variant="neutral" onClick={onCancel}>Cancel</PillButton>
      </div>
    </form>
  );
};

export default GoalManagement;
